import { ExclamationCircleFilled } from '@ant-design/icons';
import styled from 'styled-components';

import { Descriptions, FormatItem } from '../../../../../components/index';
import {
  ChargeType,
  FareStatusType,
  FareType,
} from '../../../../../typesFinancial';
import { getRmMovement } from '../../../../../utils/lists';
import { maskString } from '../../../../../utils/utils';
import { FareRevenueCode } from '../../FareRevenueCode';
import { Section, SectionCard } from './styles';

type RmTransmissionSectionProps = {
  fare: FareType;
  charge: ChargeType;
};

const { Item } = Descriptions;

const MissingValue = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--error-medium);
  font-size: 13px;
  font-weight: 600;
`;

function getOrderNumber(charge: ChargeType, fare: FareType) {
  if (charge.charge_type === 'DOCKING') {
    return charge.docking?.code;
  }
  if (charge.charge_type === 'SINGLE_FARE') {
    return fare.id ? String(fare.id) : undefined;
  }
  if (charge.charge_type === 'VESSEL') {
    return charge.vessel?.imo;
  }
  return undefined;
}

const sentStatuses: FareStatusType[] = [
  'PROCESSING',
  'BILLED',
  'REBILLED',
  'SEND_ERROR',
];

// O movimento só existe depois de um envio, então a tarifa a faturar não mostra
// nada: a escolha dela está nos botões. Na estornada, é o do envio estornado,
// para o faturista lembrar como ela foi antes de escolher de novo. Tarifa
// enviada antes da substituição existir vem com `is_substitution` falso, que é
// o pré-faturamento, para onde todo envio ia.
function getMovementLabel(fare: FareType) {
  if (fare.status === 'REVERSED') {
    return 'Último envio';
  }
  if (fare.status && sentStatuses.includes(fare.status)) {
    return 'Movimento no RM';
  }
  return undefined;
}

export function RmTransmissionSection({
  fare,
  charge,
}: RmTransmissionSectionProps) {
  const cnpj = fare.company?.cnpj;
  const hasProtocol =
    (fare.status === 'BILLED' || fare.status === 'REBILLED') &&
    !!fare.rm_record_key;
  const movementLabel = getMovementLabel(fare);
  const movement = getRmMovement(fare.is_substitution);
  const columnCount = 3 + (movementLabel ? 1 : 0) + (hasProtocol ? 1 : 0);

  return (
    <Section>
      <span className="section-title">Envio ao TOTVS RM</span>
      <SectionCard>
        <Descriptions column={columnCount}>
          <Item label="Cliente no RM (CNPJ)">
            {cnpj ? (
              <FormatItem style={{ fontFamily: 'Consolas' }}>
                {maskString(cnpj, '##.###.###/####-##')}
              </FormatItem>
            ) : (
              <MissingValue>
                <ExclamationCircleFilled />
                Empresa sem CNPJ, o RM recusa o envio
              </MissingValue>
            )}
          </Item>
          <Item label="Despesa/Receita">
            <FareRevenueCode fare={fare} />
          </Item>
          <Item label="Nº de ordem">
            <FormatItem style={{ fontFamily: 'Consolas' }}>
              {getOrderNumber(charge, fare)}
            </FormatItem>
          </Item>
          {movementLabel && (
            <Item label={movementLabel}>
              {movement.name} ({movement.code})
            </Item>
          )}
          {hasProtocol && (
            <Item label="Protocolo">
              <FormatItem style={{ fontFamily: 'Consolas' }}>
                {fare.rm_record_key}
              </FormatItem>
            </Item>
          )}
        </Descriptions>
      </SectionCard>
    </Section>
  );
}
